import { appConfig, chainSlug } from "@/constants";
import type { Erc20Token } from "@/types";
import axios from "axios";
import type { Eip1193Provider } from "ethers";
import { type Address, createPublicClient, custom } from "viem";
import { immutableZkEvm, immutableZkEvmTestnet } from "viem/chains";
import type { MinimalCoinBalance } from "../types";

// Minimal ERC20 ABI for balanceOf - only what we need for multicall
const ERC20_BALANCE_OF_ABI = [
  {
    inputs: [
      {
        internalType: "address",
        name: "account",
        type: "address",
      },
    ],
    name: "balanceOf",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
] as const;

interface TokenListPage {
  next_cursor: string | null;
  previous_cursor: string | null;
}

interface TokenListResponse {
  result: Erc20Token[];
  page: TokenListPage;
}

/**
 * Fetches all supported zkEVM tokens from the Blockchain Data API.
 * Handles pagination to get all tokens.
 */
async function fetchZkEvmTokens(): Promise<
  Array<{
    symbol: string;
    address: Address;
    decimals: number;
    name: string;
    imageUrl: string;
  }>
> {
  const baseUrl = `${appConfig.IMMUTABLE_BASE_URI}/v1/chains/${chainSlug}`;
  const tokens: Array<{
    symbol: string;
    address: Address;
    decimals: number;
    name: string;
    imageUrl: string;
  }> = [];

  let cursor: string | null = null;
  let hasMore = true;

  while (hasMore) {
    try {
      const params = new URLSearchParams({
        verification_status: "verified",
        page_size: "200",
      });
      if (cursor) {
        params.append("page_cursor", cursor);
      }

      const response = await axios.get<TokenListResponse>(
        `${baseUrl}/tokens?${params.toString()}`,
      );

      const tokenList = response.data.result || [];
      for (const token of tokenList) {
        // Skip tokens without required fields
        if (
          !token.contract_address ||
          !token.symbol ||
          token.decimals === undefined
        ) {
          continue;
        }

        // Build image URL - use token's image_url if available, otherwise construct from address
        const imageUrl =
          token.image_url ||
          `${appConfig.TOKEN_ICONS_URL}/${token.contract_address.toLowerCase()}.svg`;

        tokens.push({
          symbol: token.symbol,
          address: token.contract_address.toLowerCase() as Address,
          decimals: token.decimals,
          name: token.name || token.symbol,
          imageUrl,
        });
      }

      // Check if there are more pages
      cursor = response.data.page?.next_cursor || null;
      hasMore = !!cursor && cursor !== "";
    } catch (error) {
      console.error("Failed to fetch zkEVM tokens:", error);
      // Return what we have so far rather than failing completely
      break;
    }
  }

  return tokens;
}

/**
 * Fetches zkEVM balances using viem multicall.
 * Queries native IMX balance and ERC20 token balances in batch.
 * Fetches supported tokens from Blockchain Data API instead of using hardcoded lists.
 */
export async function getZkEvmBalances(
  provider: Eip1193Provider,
  walletAddress: Address,
  chainId: number,
): Promise<MinimalCoinBalance[]> {
  const chain =
    chainId === immutableZkEvm.id ? immutableZkEvm : immutableZkEvmTestnet;

  const publicClient = createPublicClient({
    chain,
    transport: custom(provider),
  });

  // Fetch supported tokens from Blockchain Data API
  const tokensToQuery = await fetchZkEvmTokens();

  // Get native IMX balance
  const nativeBalance = await publicClient.getBalance({
    address: walletAddress,
  });

  const balances: MinimalCoinBalance[] = [];

  // Add native IMX balance if > 0
  if (nativeBalance > 0n) {
    balances.push({
      symbol: chainId === immutableZkEvm.id ? "IMX" : "tIMX",
      contractAddress: "native",
      decimals: 18,
      balance: nativeBalance.toString(),
      name: chainId === immutableZkEvm.id ? "IMX" : "tIMX",
      imageUrl: `${appConfig.TOKEN_ICONS_URL}/imx.svg`,
    });
  }

  // Batch query ERC20 token balances using multicall
  if (tokensToQuery.length > 0) {
    const balanceCalls = tokensToQuery.map((token) => ({
      address: token.address,
      abi: ERC20_BALANCE_OF_ABI,
      functionName: "balanceOf" as const,
      args: [walletAddress],
    }));

    const results = await publicClient.multicall({
      contracts: balanceCalls,
    });

    // Process results and add non-zero balances
    results.forEach((result, index) => {
      if (
        result.status === "success" &&
        result.result &&
        typeof result.result === "bigint" &&
        result.result > 0n
      ) {
        const token = tokensToQuery[index];
        balances.push({
          symbol: token.symbol,
          contractAddress: token.address,
          decimals: token.decimals,
          balance: result.result.toString(),
          name: token.name,
          imageUrl: token.imageUrl,
        });
      }
    });
  }

  return balances;
}
